import { useEffect, useState, useMemo } from "react";
import axios from "axios";
import AdminLayout from "../../components/admin/AdminLayout";
import { useAuth } from "../../context/AuthContext";
import { useProject } from "../../context/ProjectContext";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Cell,
  PieChart,
  Pie,
  Legend,
  Radar,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
} from "recharts";
import { Eye, Users, Globe2, FileText, RefreshCw, TrendingUp, Trophy } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const COLORS = ["#0F172A", "#3B82F6", "#10B981", "#F59E0B", "#EF4444", "#8B5CF6", "#EC4899", "#14B8A6"];

const AdminGlobalAnalytics = () => {
  const { token } = useAuth();
  const { projects } = useProject();
  const auth = { headers: { Authorization: `Bearer ${token}` } };
  const [days, setDays] = useState(30);
  const [combined, setCombined] = useState(null);
  const [perProjectFull, setPerProjectFull] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      // Combined summary
      const c = await axios.get(`${API}/analytics/summary`, { ...auth, params: { project_id: "combined", days } });
      setCombined(c.data);
      // Per-project deep stats (fire in parallel)
      const calls = projects.map((p) =>
        axios.get(`${API}/analytics/summary`, { ...auth, params: { project_id: p.id, days } })
          .then((r) => ({ project: p, data: r.data }))
          .catch(() => ({ project: p, data: null }))
      );
      const all = await Promise.all(calls);
      setPerProjectFull(all);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (projects.length === 0) return;
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days, projects.length]);

  // Comparison table data
  const comparison = useMemo(() => {
    return perProjectFull.map(({ project, data }) => {
      const tv = data?.total_views || 0;
      const uv = data?.unique_visitors || 0;
      return {
        id: project.id,
        name: project.name,
        domain: project.domain,
        views: tv,
        unique: uv,
        countries: data?.top_countries?.length || 0,
        avg_views_per_visitor: uv > 0 ? +(tv / uv).toFixed(2) : 0,
        top_country: data?.top_countries?.[0]?.label || "—",
        top_referrer: (data?.top_referrers?.[0]?.label) || "(direct)",
        top_page: data?.top_pages?.[0]?.label || "—",
      };
    }).sort((a, b) => b.views - a.views);
  }, [perProjectFull]);

  const totalViews = combined?.total_views || 0;
  const totalUnique = combined?.unique_visitors || 0;
  const totalProjects = projects.length;
  const topProject = comparison[0];

  // Stacked timeline per project
  const stackedTimeline = useMemo(() => {
    const dateMap = {};
    perProjectFull.forEach(({ project, data }) => {
      (data?.timeline || []).forEach((row) => {
        if (!dateMap[row.date]) dateMap[row.date] = { date: row.date };
        dateMap[row.date][project.name] = row.count;
      });
    });
    return Object.values(dateMap).sort((a, b) => (a.date > b.date ? 1 : -1));
  }, [perProjectFull]);

  // Radar comparison (normalized metrics 0–100 per project)
  const radarData = useMemo(() => {
    if (comparison.length === 0) return [];
    const maxViews = Math.max(...comparison.map((c) => c.views), 1);
    const maxUnique = Math.max(...comparison.map((c) => c.unique), 1);
    const maxCountries = Math.max(...comparison.map((c) => c.countries), 1);
    const maxRatio = Math.max(...comparison.map((c) => c.avg_views_per_visitor), 1);
    return [
      { metric: "Views",      ...Object.fromEntries(comparison.map((c) => [c.name, Math.round((c.views / maxViews) * 100)])) },
      { metric: "Unique",     ...Object.fromEntries(comparison.map((c) => [c.name, Math.round((c.unique / maxUnique) * 100)])) },
      { metric: "Countries",  ...Object.fromEntries(comparison.map((c) => [c.name, Math.round((c.countries / maxCountries) * 100)])) },
      { metric: "Engagement", ...Object.fromEntries(comparison.map((c) => [c.name, Math.round((c.avg_views_per_visitor / maxRatio) * 100)])) },
    ];
  }, [comparison]);

  return (
    <AdminLayout title="Global Analytics">
      <div className="space-y-6" data-testid="admin-global-analytics">
        {/* Controls */}
        <Card>
          <CardContent className="p-4 flex flex-col md:flex-row gap-4 items-center">
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-slate-600">Window:</label>
              <Select value={String(days)} onValueChange={(v) => setDays(Number(v))}>
                <SelectTrigger className="w-32" data-testid="global-analytics-days">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">Last 24h</SelectItem>
                  <SelectItem value="7">Last 7 days</SelectItem>
                  <SelectItem value="30">Last 30 days</SelectItem>
                  <SelectItem value="90">Last 90 days</SelectItem>
                  <SelectItem value="365">Last year</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <p className="text-xs text-slate-500 ml-auto">
              Aggregating <span className="font-medium text-slate-700">{totalProjects} site{totalProjects === 1 ? "" : "s"}</span>
            </p>
            <Button variant="outline" onClick={load} disabled={loading} data-testid="refresh-global-analytics">
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </Button>
          </CardContent>
        </Card>

        {/* Combined KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Kpi icon={<Eye className="h-5 w-5" />} label="Total views (all sites)" value={totalViews} />
          <Kpi icon={<Users className="h-5 w-5" />} label="Unique visitors" value={totalUnique} />
          <Kpi icon={<Globe2 className="h-5 w-5" />} label="Active sites" value={totalProjects} />
          <Kpi
            icon={<Trophy className="h-5 w-5" />}
            label="Top performer"
            value={topProject?.name || "—"}
            text
          />
        </div>

        {/* Head-to-head table */}
        <Card>
          <CardHeader><CardTitle>Site-by-site comparison</CardTitle></CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="comparison-table">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b">
                    <th className="py-2 pr-3">Site</th>
                    <th className="py-2 pr-3 text-right">Views</th>
                    <th className="py-2 pr-3 text-right">Unique</th>
                    <th className="py-2 pr-3 text-right">Views / visitor</th>
                    <th className="py-2 pr-3 text-right">Countries</th>
                    <th className="py-2 pr-3">Top country</th>
                    <th className="py-2 pr-3">Top page</th>
                  </tr>
                </thead>
                <tbody>
                  {comparison.map((c, i) => (
                    <tr key={c.id} className="border-b last:border-b-0" data-testid={`comparison-row-${c.id}`}>
                      <td className="py-3 pr-3">
                        <div className="flex items-center gap-2">
                          <span className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS[i % COLORS.length] }} />
                          <div>
                            <p className="font-medium">{c.name}</p>
                            {c.domain && <p className="text-xs text-slate-500">{c.domain}</p>}
                          </div>
                          {i === 0 && c.views > 0 && <Badge className="bg-amber-100 text-amber-700 ml-2">Top</Badge>}
                        </div>
                      </td>
                      <td className="py-3 pr-3 text-right font-semibold">{c.views.toLocaleString()}</td>
                      <td className="py-3 pr-3 text-right">{c.unique.toLocaleString()}</td>
                      <td className="py-3 pr-3 text-right">{c.avg_views_per_visitor}</td>
                      <td className="py-3 pr-3 text-right">{c.countries}</td>
                      <td className="py-3 pr-3">{c.top_country}</td>
                      <td className="py-3 pr-3 max-w-xs truncate text-slate-500">{c.top_page}</td>
                    </tr>
                  ))}
                  {comparison.length === 0 && (
                    <tr><td colSpan="7" className="py-6 text-center text-slate-500">No data yet.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Per-site share bars */}
        <Card>
          <CardHeader><CardTitle>Traffic share</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-2">
              {comparison.map((c, i) => {
                const total = comparison.reduce((s, x) => s + x.views, 0);
                const pct = total > 0 ? (c.views / total) * 100 : 0;
                return (
                  <div key={c.id} className="flex items-center gap-3">
                    <div className="w-44 shrink-0">
                      <p className="font-medium truncate">{c.name}</p>
                      {c.domain && <p className="text-xs text-slate-500 truncate">{c.domain}</p>}
                    </div>
                    <div className="flex-1 h-3 bg-slate-100 rounded-full overflow-hidden">
                      <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: COLORS[i % COLORS.length] }} />
                    </div>
                    <div className="w-28 text-right">
                      <span className="font-semibold">{c.views}</span>
                      <span className="text-xs text-slate-500 ml-1">({pct.toFixed(0)}%)</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Stacked timeline */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Daily traffic per site</CardTitle>
              <TrendingUp className="h-5 w-5 text-slate-400" />
            </div>
          </CardHeader>
          <CardContent style={{ height: 320 }}>
            {stackedTimeline.length === 0 ? (
              <p className="text-slate-500 flex items-center justify-center h-full">No data in selected window.</p>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={stackedTimeline}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                  <XAxis dataKey="date" stroke="#94A3B8" fontSize={12} />
                  <YAxis stroke="#94A3B8" fontSize={12} />
                  <Tooltip />
                  <Legend />
                  {projects.map((p, i) => (
                    <Line key={p.id} type="monotone" dataKey={p.name} stroke={COLORS[i % COLORS.length]} strokeWidth={2} dot={false} />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        {/* Radar — normalized performance metrics */}
        <Card>
          <CardHeader><CardTitle>Performance radar (normalized 0–100)</CardTitle></CardHeader>
          <CardContent style={{ height: 360 }}>
            {radarData.length === 0 ? (
              <p className="text-slate-500 flex items-center justify-center h-full">No data.</p>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <RadarChart data={radarData}>
                  <PolarGrid />
                  <PolarAngleAxis dataKey="metric" />
                  <PolarRadiusAxis angle={30} domain={[0, 100]} />
                  {projects.map((p, i) => (
                    <Radar
                      key={p.id}
                      name={p.name}
                      dataKey={p.name}
                      stroke={COLORS[i % COLORS.length]}
                      fill={COLORS[i % COLORS.length]}
                      fillOpacity={0.18}
                    />
                  ))}
                  <Legend />
                  <Tooltip />
                </RadarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        {/* Aggregated cuts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <DonutCard title="Top countries (all sites)" rows={combined?.top_countries || []} />
          <BarCard title="Browsers (all sites)" rows={combined?.browsers || []} />
        </div>
      </div>
    </AdminLayout>
  );
};

const Kpi = ({ icon, label, value, text }) => (
  <Card>
    <CardContent className="p-5">
      <div className="flex items-center gap-3 text-slate-500 text-sm mb-2">{icon}<span>{label}</span></div>
      <p className={`font-bold text-slate-900 ${text ? "text-xl truncate" : "text-3xl"}`}>{text ? value : Number(value).toLocaleString()}</p>
    </CardContent>
  </Card>
);

const DonutCard = ({ title, rows }) => {
  const data = (rows || []).slice(0, 8).map((r) => ({ name: r.label || "Unknown", value: r.count }));
  return (
    <Card>
      <CardHeader><CardTitle className="text-base">{title}</CardTitle></CardHeader>
      <CardContent style={{ height: 240 }}>
        {data.length === 0 ? (
          <p className="text-slate-500 text-sm">No data.</p>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie data={data} dataKey="value" nameKey="name" innerRadius={50} outerRadius={80} paddingAngle={2}>
                {data.map((d, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Tooltip />
              <Legend wrapperStyle={{ fontSize: 12 }} />
            </PieChart>
          </ResponsiveContainer>
        )}
      </CardContent>
    </Card>
  );
};

const BarCard = ({ title, rows }) => {
  const data = (rows || []).slice(0, 8).map((r) => ({ name: r.label || "Other", value: r.count }));
  return (
    <Card>
      <CardHeader><CardTitle className="text-base">{title}</CardTitle></CardHeader>
      <CardContent style={{ height: 240 }}>
        {data.length === 0 ? (
          <p className="text-slate-500 text-sm">No data.</p>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis dataKey="name" stroke="#94A3B8" fontSize={11} />
              <YAxis stroke="#94A3B8" fontSize={11} />
              <Tooltip />
              <Bar dataKey="value" fill="#3B82F6" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </CardContent>
    </Card>
  );
};

export default AdminGlobalAnalytics;
